import { formatBytes, formatDuration } from "../lib/tasks";
import type { FileTask, TeamForm, UploadStats } from "../lib/types";

interface Props {
  form: TeamForm;
  stats: UploadStats;
  /**
   * Only the failed tasks. The full task list is deliberately never rendered -
   * 1.000+ rows locks the tab, and the aggregate is what the user acts on.
   */
  failed: FileTask[];
  finished: boolean;
  paused: boolean;
  onPause: () => void;
  onRetryFailed: () => void;
  onNewUpload: () => void;
}

export function UploadProgress({
  form,
  stats,
  failed,
  finished,
  paused,
  onPause,
  onRetryFailed,
  onNewUpload,
}: Props) {
  const settled = stats.done + stats.skipped;
  const pct = stats.bytesTotal > 0 ? (stats.bytesDone / stats.bytesTotal) * 100 : 0;
  const kindLabel = form.dataKind === "thermal" ? "Termal" : "RGB";

  return (
    <div className="card">
      <h2>3. Yükleme</h2>
      <p className="muted">
        {form.provinceName} · {form.teamName} · <strong>{kindLabel}</strong>
      </p>

      <div className="progress" role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
        <div className="bar" style={{ width: `${pct}%` }} />
      </div>

      <div className="summary">
        <div className="stat">
          <span className="big">
            {settled.toLocaleString("tr-TR")} / {stats.total.toLocaleString("tr-TR")}
          </span>
          <span className="muted">görüntü</span>
        </div>
        <div className="stat">
          <span className="big">
            {formatBytes(stats.bytesDone)} / {formatBytes(stats.bytesTotal)}
          </span>
          <span className="muted">veri</span>
        </div>
        <div className="stat">
          <span className="big">
            {stats.bytesPerSecond ? `${formatBytes(stats.bytesPerSecond)}/sn` : "—"}
          </span>
          <span className="muted">hız</span>
        </div>
        <div className="stat">
          <span className="big">
            {finished ? "—" : stats.etaSeconds !== null ? formatDuration(stats.etaSeconds) : "—"}
          </span>
          <span className="muted">kalan süre</span>
        </div>
      </div>

      {stats.skipped > 0 && (
        <p className="muted small">
          {stats.skipped.toLocaleString("tr-TR")} görüntü sunucuda zaten mevcuttu, tekrar
          yüklenmedi.
        </p>
      )}

      {!finished && !paused && (
        <p className="notice">
          Yükleme sürerken bu sekmeyi kapatmayın. Bağlantı koparsa sayfayı yenileyip kaldığınız
          yerden devam edebilirsiniz.
        </p>
      )}

      {finished && stats.failed === 0 && (
        <p className="ok">
          ✓ Yükleme tamamlandı. {settled.toLocaleString("tr-TR")} görüntü teslim edildi.
        </p>
      )}

      {stats.failed > 0 && (
        <div className="failures">
          <p className="warn">
            {stats.failed.toLocaleString("tr-TR")} görüntü yüklenemedi.
            {finished ? " Tekrar denemek için aşağıdaki düğmeyi kullanın." : ""}
          </p>
          <ul>
            {failed.slice(0, 50).map((t) => (
              <li key={t.id}>
                <code>{t.file.name}</code> <span className="muted">— {t.error}</span>
              </li>
            ))}
          </ul>
          {failed.length > 50 && (
            <p className="muted small">…ve {failed.length - 50} dosya daha.</p>
          )}
        </div>
      )}

      <div className="actions">
        {!finished && (
          <button type="button" onClick={onPause}>
            {paused ? "Durduruldu" : "Duraklat"}
          </button>
        )}
        {finished && stats.failed > 0 && (
          <button type="button" className="primary" onClick={onRetryFailed}>
            Başarısızları tekrar dene ({stats.failed})
          </button>
        )}
        {finished && (
          <button type="button" onClick={onNewUpload}>
            Yeni yükleme
          </button>
        )}
      </div>
    </div>
  );
}
